import React, { useState } from 'react';
import { Box, Card, Avatar, Typography, IconButton, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme, alpha, Stack } from '@mui/material';
import { MdLogout, MdMenu, MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { getOrgTokens } from '../../../theme/style/organizationPalette';
import muiStyle from '../../../style/components-style/muiStyle';

const ProfileLayout = ({
    children,
    user,
    username,
    isAdmin,
    isCompanyAdmin,
    activeTab,
    setActiveTab,
    menuGroups,
    attemptLogout,
    handleLogout,
    mode,
    primarySolid,
    primaryAlpha,
    quotaData = { used: 0, total: 1000 }
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const isDark = mode === 'dark';
    const tok = getOrgTokens(isDark);

    const consumptionPct = (quotaData.used / (quotaData.total || 1)) * 100;
    const quotaColor = consumptionPct > 90 ? theme.palette.error.main : (consumptionPct > 70 ? theme.palette.warning.main : theme.palette.primary.main);
    const displayPct = Math.min(Math.round(consumptionPct), 100);

    // Arivu Brand Palette for Tab Highlighting
    const arivuGreen = "#659d33";

    const MenuItem = ({ item, isActive }) => (
        <ListItemButton
            onClick={() => {
                setActiveTab(item.index);
                if (isMobile) setMobileDrawerOpen(false);
            }}
            selected={isActive}
            sx={{
                borderRadius: '10px',
                cursor: 'pointer',
                bgcolor: isActive ? alpha(arivuGreen, 0.12) : 'transparent',
                color: isActive ? arivuGreen : tok.text.secondary,
                mb: 1,
                py: 1.2,
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    bgcolor: isActive ? alpha(arivuGreen, 0.12) : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    transform: 'translateX(4px)'
                },
                position: 'relative',
                '&::before': isActive ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 4,
                    height: 18,
                    bgcolor: arivuGreen,
                    borderRadius: '0 4px 4px 0',
                    boxShadow: `2px 0 8px ${alpha(arivuGreen, 0.4)}`
                } : {}
            }}
        >
            <ListItemIcon sx={{
                minWidth: 38,
                color: isActive ? arivuGreen : tok.text.secondary,
                transition: 'all 0.25s'
            }}>
                {React.cloneElement(item.icon, { size: 20 })}
            </ListItemIcon>
            <ListItemText
                primary={t(item.label)}
                primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '14px',
                    letterSpacing: '0.2px',
                    color: isActive ? arivuGreen : tok.text.secondary,
                    fontFamily: tok.font.family
                }}
            />
        </ListItemButton>
    );

    const SidebarContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {menuGroups.map((group, gIndex) => {
                if (!group.items || group.items.length === 0) return null;
                return (
                    <Box key={gIndex}>
                        {group.title && (
                            <Typography variant="caption" sx={{
                                px: 2,
                                pb: 1.5,
                                display: 'block',
                                fontWeight: 700,
                                color: tok.text.muted,
                                opacity: 0.6,
                                fontSize: '11px',
                                letterSpacing: '0.4px',
                                textTransform: 'none',
                                fontFamily: tok.font.family
                            }}>
                                {t(group.title)}
                            </Typography>
                        )}
                        <Box sx={{
                            bgcolor: tok.bg.card,
                            borderRadius: tok.radius.card,
                            border: '1px solid',
                            borderColor: tok.border.card,
                            p: 1.2,
                            boxShadow: tok.shadow.card,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: alpha(arivuGreen, 0.4),
                                boxShadow: tok.shadow.cardHover,
                            }
                        }}>
                            <List sx={{ p: 0 }}>
                                {group.items.map((item, iIndex) => (
                                    <MenuItem key={iIndex} item={item} isActive={activeTab === item.index} />
                                ))}
                            </List>
                        </Box>
                    </Box>
                );
            })}

            <Box sx={{ mt: 'auto', p: 1 }}>
                <ListItemButton
                    onClick={() => attemptLogout(handleLogout)}
                    sx={{
                        borderRadius: '10px',
                        color: alpha(theme.palette.error.main, 0.9),
                        py: 1.2,
                        transition: "all 0.2s",
                        "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.05),
                            transform: 'translateX(4px)'
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 38, color: alpha(theme.palette.error.main, 0.9) }}>
                        <MdLogout size={20} />
                    </ListItemIcon>
                    <ListItemText
                        primary={t("Sign Out")}
                        primaryTypographyProps={{
                            fontWeight: tok.font.weight.bold,
                            fontSize: '14px',
                            fontFamily: tok.font.family
                        }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            ...muiStyle.mainContainer,
            backgroundColor: tok.bg.page,
            minHeight: "100vh",
            px: 0, py: 0,
            overflow: "hidden"
        }}>
            <Box sx={{
                width: "100%",
                pt: 6,
                pb: 8,
                px: { xs: 2, md: 5 }
            }}>
                {/* Header */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3.5,
                    mb: 4,
                    justifyContent: 'space-between',
                    flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3.5 }}>
                        <Avatar
                            sx={{
                                width: { xs: 65, md: 70 },
                                height: { xs: 65, md: 70 },
                                bgcolor: primarySolid,
                                fontSize: "24px",
                                fontWeight: 700,
                            }}
                        >
                            {username ? username.charAt(0).toUpperCase() : "U"}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" sx={{
                                fontWeight: tok.font.weight.bold,
                                color: tok.text.primary,
                                fontFamily: tok.font.family,
                                letterSpacing: "-0.02em",
                                fontSize: tok.font.size.tableTitle
                            }}>
                                {username ? username.charAt(0).toUpperCase() + username.slice(1) : t("Settings")}
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: tok.text.muted,
                                fontWeight: 600,
                                fontFamily: tok.font.family,
                                fontSize: tok.font.size.caption,
                                textTransform: 'none',
                                letterSpacing: '0.2px'
                            }}>
                                {isAdmin ? t("Master Admin") : (isCompanyAdmin ? t("Company Administrator") : t("User"))}
                                {user?.company_name ? ` • ${user.company_name}` : ""}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        pr: 3,
                        minWidth: { xs: '100%', sm: 280 },
                        bgcolor: isDark ? alpha(primarySolid, 0.05) : alpha(primarySolid, 0.02),
                        px: 3,
                        py: 1.8,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: isDark ? alpha(primarySolid, 0.25) : alpha(primarySolid, 0.15),
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: -6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 6,
                            height: 24,
                            bgcolor: isDark ? alpha(primarySolid, 0.3) : alpha(primarySolid, 0.2),
                            borderRadius: '0 4px 4px 0',
                            border: '1px solid',
                            borderColor: isDark ? alpha(primarySolid, 0.25) : alpha(primarySolid, 0.15),
                            borderLeft: 'none'
                        },
                        boxShadow: tok.shadow.iconBox,
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${100 - displayPct}%`,
                            bgcolor: alpha(quotaColor, isDark ? 0.08 : 0.05),
                            zIndex: 0,
                            transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRight: `2px solid ${alpha(quotaColor, 0.3)}`
                        }} />

                        <Stack direction="row" spacing={3} alignItems="center" sx={{ zIndex: 1, position: 'relative', width: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                                <Typography sx={{
                                    fontSize: '22px',
                                    fontWeight: 700,
                                    color: quotaColor,
                                    fontFamily: tok.font.family,
                                    lineHeight: 1
                                }}>
                                    {100 - displayPct}%
                                </Typography>
                                <Typography sx={{
                                    fontSize: '9px',
                                    fontWeight: 800,
                                    color: tok.text.muted,
                                    textTransform: 'capitalize',
                                    letterSpacing: '0.5px'
                                }}>
                                    {t("Remaining")}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: tok.font.weight.bold,
                                    color: tok.text.muted,
                                    display: 'block',
                                    mb: 0.2,
                                    fontSize: '11px',
                                    fontFamily: tok.font.family,
                                    letterSpacing: '0.2px'
                                }}>
                                    {t("Consumption Status")}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography variant="h5" sx={{
                                        fontWeight: tok.font.weight.bold,
                                        lineHeight: 1,
                                        color: tok.text.primary,
                                        fontFamily: tok.font.family,
                                        fontSize: tok.font.size.gridCell
                                    }}>
                                        {quotaData.used.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontWeight: tok.font.weight.medium,
                                        color: tok.text.muted,
                                        fontFamily: tok.font.family,
                                        fontSize: '12px'
                                    }}>
                                        / {quotaData.total.toLocaleString()} pts
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>

                    {isMobile && (
                        <IconButton
                            onClick={() => setMobileDrawerOpen(true)}
                            sx={{
                                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }
                            }}
                        >
                            <MdMenu size={24} />
                        </IconButton>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 5, flexDirection: { xs: "column", md: "row" } }}>
                    {!isMobile ? (
                        <Box sx={{ width: { xs: "100%", md: 280 }, position: "sticky", top: 40, height: 'fit-content' }}>
                            <SidebarContent />
                        </Box>
                    ) : (
                        <Drawer
                            anchor="left"
                            open={mobileDrawerOpen}
                            onClose={() => setMobileDrawerOpen(false)}
                            sx={{
                                '& .MuiDrawer-paper': {
                                    width: 280,
                                    p: 2,
                                    bgcolor: theme.palette.background.page
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{t("Account Menu")}</Typography>
                                <IconButton onClick={() => setMobileDrawerOpen(false)}>
                                    <MdClose size={24} />
                                </IconButton>
                            </Box>
                            <SidebarContent />
                        </Drawer>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ProfileLayout;
